<template>
  <BaseModal
    :name="UNLOCK_FULL_ACCESS_MODAL_NAME"
    width-class="tw-max-w-[1200px] tw-relative"
    @hide="emit('hide')"
    @show="emit('show')"
  >
    <div class="tw-space-y-6 tw-p-8">
      <div
        v-if="modalState && Object.keys(modalState).length > 0"
        class="tw-flex tw-flex-col tw-items-center"
      >
        <Avatar :src="modalState.avatarUrl" size="3xl" square shadow="lg" />

        <div class="tw-mt-8 tw-text-2xl tw-font-bold md:tw-text-3xl">
          {{ modalState.displayName }}
        </div>

        <div class="tw-mt-2 tw-text-center tw-text-lg tw-text-neutral-600">
          {{ modalState.professionTitle }}
        </div>

        <div class="tw-mt-2 tw-text-center tw-text-sm tw-text-neutral-400">
          <div class="tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-2">
            <span class="tw-inline-flex tw-items-center tw-gap-1.5">
              <LocationIcon class="tw--mt-0.5 tw-h-[15px] tw-w-[11px]" />
              <span>{{ modalState.livingInWithTimezone }}</span>
            </span>
            <template
              v-if="modalState.languageList && modalState.languageList.length"
            >
              <span>•</span>
              <span class="tw-inline-flex tw-items-center tw-gap-1.5">
                <GlobeIcon class="tw-size-4" />
                <span>{{ modalState.languageList.join(', ') }}</span>
              </span>
            </template>
            <span>•</span>
            <span>
              {{ modalState.fromDetails }}
            </span>
          </div>

          <div
            v-if="modalState.respondsIn"
            class="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1.5"
          >
            <LightningIcon class="tw-size-3.5 tw-text-success-500" />
            Usually responds in {{ modalState.respondsIn }}
          </div>
        </div>
      </div>

      <div
        v-else
        class="tw-text-center tw-text-2xl tw-font-bold md:tw-text-4xl"
      >
        Unlock Full Access
      </div>

      <div class="tw-text-center tw-text-xl tw-font-semibold">
        <p v-if="modalState?.firstName">
          To connect with {{ modalState.firstName }} and {{ totalMentors }}+
          other mentors, choose your membership:
        </p>
        <p v-else>
          To connect with {{ totalMentors }}+ mentors, choose your membership:
        </p>
      </div>

      <MembershipSubscriptions
        with-subscription-benefits
        disable-billing-cycle-toggle
        type="unlock-full-access-modal"
        class="tw-w-full"
      />

      <p class="tw-mx-auto tw-max-w-lg tw-text-xs tw-text-neutral-500">
        Unlock full access to book calls with mentors by purchasing a
        GrowthMentor membership.
      </p>

      <div
        v-if="modalState && modalState.featuredReview"
        class="tw-flex tw-flex-col tw-items-center tw-rounded-xl tw-bg-secondary-50 tw-px-10 tw-py-5"
      >
        <QuoteIcon class="tw-size-6 tw-text-secondary-500" />

        <p class="tw-mt-2 tw-text-center tw-text-lg">
          {{ modalState.featuredReview.comment }}
        </p>

        <div class="tw-mt-4 tw-flex tw-items-center tw-space-x-2">
          <Avatar
            :src="modalState.featuredReview.authorAvatarUrl"
            size="md"
            class="tw-shrink-0"
          />

          <div>
            <div class="tw-text-sm tw-font-semibold">
              {{ modalState.featuredReview.authorName }}
            </div>
            <div class="tw-text-xs tw-text-neutral-500">
              {{ modalState.featuredReview.authorTitle }}
            </div>
          </div>
        </div>
      </div>

      <div class="!tw-mt-12 tw-flex tw-flex-col tw-items-center">
        <StackedAvatars :avatars="avatars" size="lg" />
        <div class="tw-mt-6 tw-text-lg tw-font-semibold">
          {{ totalMentors }}+ mentors available
        </div>
        <p
          v-if="modalState?.firstName"
          class="tw-mt-4 tw-text-sm tw-text-neutral-500"
        >
          Join GrowthMentor to connect with experts like
          {{ modalState.firstName }}
        </p>
      </div>
    </div>

    <IconButton
      class="tw-absolute tw-right-4 tw-top-4"
      @click="closeModal(UNLOCK_FULL_ACCESS_MODAL_NAME)"
    >
      <CloseIcon />
    </IconButton>
  </BaseModal>
</template>

<script lang="ts">
export type UnlockFullAccessModalState = {
  avatarUrl: string
  displayName: string
  firstName: string
  professionTitle: string
  livingInWithTimezone: string
  languageList: string[]
  fromDetails: string
  respondsIn: string
  featuredReview: {
    comment: string
    authorName: string
    authorAvatarUrl: string
    authorTitle: string
  }
}
</script>

<script setup lang="ts">
import IconButton from '@/shared/components/IconButton.vue'
import CloseIcon from '@/assets/images/icons/close.svg'
import QuoteIcon from '@/assets/images/icons/quotes.svg'
import LocationIcon from '@/assets/images/icons/location.svg'
import GlobeIcon from '@/assets/images/icons/globe.svg'
import LightningIcon from '@/assets/images/icons/lightning.svg'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import StackedAvatars from '@/shared/components/StackedAvatars.vue'
import { useScreenWidth } from '@/shared/composables/useScreenWidth.js'
import { computed, onBeforeMount, provide, ref, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useModals } from '@/shared/composables/useModals.js'
import BaseModal from '@/shared/components/BaseModal.vue'
import MembershipSubscriptions from '@/modules/growthmentor/wizard/components/MembershipSubscriptions.vue'
import {
  CouponCodeContext,
  DiscountedPersonalPlansType,
  DiscountedTeamPlansType,
} from '@/modules/growthmentor/wizard/types/coupon-code-context'
import { useCouponCode } from '@/shared/composables/useCouponCode'
import Avatar from '@/shared/components/avatar/Avatar.vue'

const emit = defineEmits(['hide', 'show'])

const { closeModal, getModalState } = useModals()
const { screenWidth } = useScreenWidth()
const page = usePage<{
  unlockFullAccessModalData: {
    avatars: string[]
    plans: Array<{
      id: number
      name: string
      interval: 'month' | 'year'
      intervalCount: number
      group: string
      amount: number
      amountPerMonth: {
        subtotal: number
        total: number
        tax: number
      }
    }>
    teamPlans: Array<{
      id: number
      name: string
      interval: 'month' | 'year'
      intervalCount: number
      amount: number
      currency: string
      amountInDollars: string
      mostPopular: boolean
      minimumSeats: number
      old: boolean
      tiers: Array<{
        id: number
        upTo: number
        unitAmount: number
        flatAmount: number
      }>
      maximumSeats: number
      discount: number
    }>
    totalMentors: number
    limitedTimeOffer: string | null
  }
}>()

const modalState = computed(
  () =>
    getModalState(
      UNLOCK_FULL_ACCESS_MODAL_NAME,
    ) as UnlockFullAccessModalState | null,
)

const avatars = computed(() => {
  const _avatars = Array.from(page.props.unlockFullAccessModalData.avatars)
  if (screenWidth.value < 350) {
    return _avatars.slice(0, 4)
  } else if (screenWidth.value < 400) {
    return _avatars.slice(0, 5)
  } else if (screenWidth.value < 450) {
    return _avatars.slice(0, 6)
  } else if (screenWidth.value < 500) {
    return _avatars.slice(0, 7)
  } else if (screenWidth.value < 550) {
    return _avatars.slice(0, 8)
  } else if (screenWidth.value < 600) {
    return _avatars.slice(0, 9)
  } else {
    return _avatars
  }
})
const totalMentors = computed(() => {
  return page.props.unlockFullAccessModalData.totalMentors
})

const discountedPersonalPlans = ref<DiscountedPersonalPlansType>([])
const discountedTeamPlans = ref<DiscountedTeamPlansType>([])
const {
  coupon,
  checkCouponCode,
  toggleCouponCodeInput,
  clearCouponErrors,
  couponCodeField,
} = useCouponCode()
const applyCouponCode = () => {
  checkCouponCode(
    ({
      plans,
      teamPlans,
    }: {
      plans: DiscountedPersonalPlansType
      teamPlans: DiscountedTeamPlansType
    }) => {
      discountedPersonalPlans.value = plans
      discountedTeamPlans.value = teamPlans
    },
  )
}
watch(couponCodeField, () => {
  discountedPersonalPlans.value = []
  discountedTeamPlans.value = []
})
provide<CouponCodeContext>('coupon', {
  coupon,
  couponCodeField,
  applyCouponCode,
  toggleCouponCodeInput,
  clearCouponErrors,
  discountedPersonalPlans,
  discountedTeamPlans,
})

onBeforeMount(() => {
  const couponCode = page.props.unlockFullAccessModalData.limitedTimeOffer
  if (!couponCode) return
  coupon.value.code = couponCode
  coupon.value.isLimitedTimeOffer = true
  applyCouponCode()
})
</script>
